import React, { useState } from 'react';
import {
    Paper, Typography, Grid, TextField, Button,
    Box, makeStyles, InputAdornment, Chip,
    CircularProgress, IconButton, Tooltip,
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import HistoryIcon       from '@material-ui/icons/History';
import ListAltIcon       from '@material-ui/icons/ListAlt';
import SyncIcon          from '@material-ui/icons/Sync';
import VisibilityIcon    from '@material-ui/icons/Visibility';
import CheckCircleIcon   from '@material-ui/icons/CheckCircle';
import ErrorIcon         from '@material-ui/icons/Error';
import InfoIcon          from '@material-ui/icons/Info';
import RefreshIcon       from '@material-ui/icons/Refresh';

import { reconcileStripeUsageService } from '../../../../services/organization.service';
import { toastSuccess, toastWarning } from '../../../../utils/utils';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const getDefaultParams = () => ({
    billing_month:     new Date().toISOString().substring(0, 7), // YYYY-MM
    log_lookback_days: 30,
    limit:             100,
});

/* ─── styles ──────────────────────────────────────────────────────────── */

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    paper: {
        padding: theme.spacing(4),
        marginBottom: theme.spacing(3),
        borderRadius: 12,
        border: '1px solid #eef2f6',
        backgroundColor: '#fff',
    },
    actionWrapper: {
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        // match TextField height with helperText so action row aligns with inputs
        marginBottom: 22,
    },
    actionButton: {
        height: 56,
        borderRadius: 8,
        fontWeight: 600,
        textTransform: 'none',
        fontSize: '1rem',
        flex: 1,
    },
    resetButton: {
        height: 56,
        width: 56,
        minWidth: 56,
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        color: '#64748b',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: '#f8fafc',
            borderColor: '#cbd5e1',
        },
    },
    statCard: {
        padding: theme.spacing(3, 2),
        borderRadius: 12,
        border: '1px solid #f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        backgroundColor: '#fff',
    },
}));

/* ─── component ───────────────────────────────────────────────────────── */

const PHASE = {
    IDLE:      'idle',      // nothing run yet
    PREVIEWED: 'previewed', // dry-run done, awaiting commit
    COMMITTED: 'committed', // live run done
};

const DQPBDQReconcileTab = () => {
    const classes = useStyles();
    const [params, setParams]   = useState(getDefaultParams);
    const [phase,  setPhase]    = useState(PHASE.IDLE);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    /* If params change after a preview, drop the preview so user must re-preview before committing. */
    const handleParamChange = (e) => {
        const { name, value } = e.target;
        setParams(prev => ({ ...prev, [name]: value }));
        if (phase !== PHASE.IDLE) {
            setPhase(PHASE.IDLE);
            setResults(null);
        }
    };

    const runReconcile = async (dryRun) => {
        setLoading(true);
        try {
            const formattedMonth = String(params.billing_month).replace('-', '');
            const result = await reconcileStripeUsageService(
                formattedMonth,
                parseInt(params.log_lookback_days, 10),
                parseInt(params.limit, 10),
                dryRun,
            );

            if (result && result.data) {
                setResults(result.data);
                setPhase(dryRun ? PHASE.PREVIEWED : PHASE.COMMITTED);
                toastSuccess(
                    dryRun
                        ? 'Preview ready. Review the summary, then click Reconcile Data to commit.'
                        : (result.message || 'Reconciliation applied successfully.')
                );
            } else {
                toastWarning('No results returned');
            }
        } catch (err) {
            toastWarning(err.message || 'Error running reconciliation');
        } finally {
            setLoading(false);
            console.log('--- Reconcile Process Finished ---');
        }
    };

    const handlePreview   = () => runReconcile(true);
    const handleCommit    = () => runReconcile(false);
    const handleReset     = () => {
        setPhase(PHASE.IDLE);
        setResults(null);
        setParams(getDefaultParams());
    };

    /* ─── render ──────────────────────────────────────────────────────── */

    const isPreviewed = phase === PHASE.PREVIEWED;
    const isCommitted = phase === PHASE.COMMITTED;

    return (
        <Box className={classes.root}>
            <Paper className={classes.paper} elevation={0}>
                <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Billing Month"
                            type="month"
                            name="billing_month"
                            value={params.billing_month}
                            onChange={handleParamChange}
                            fullWidth
                            variant="outlined"
                            disabled={loading}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><CalendarTodayIcon fontSize="small" color="primary" /></InputAdornment>,
                            }}
                            helperText="Format: YYYYMM"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Lookback Days"
                            type="number"
                            name="log_lookback_days"
                            value={params.log_lookback_days}
                            onChange={handleParamChange}
                            fullWidth
                            variant="outlined"
                            disabled={loading}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><HistoryIcon fontSize="small" color="primary" /></InputAdornment>,
                                inputProps: { min: 1 },
                            }}
                            helperText="Default: 15"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Batch Limit"
                            type="number"
                            name="limit"
                            value={params.limit}
                            onChange={handleParamChange}
                            fullWidth
                            variant="outlined"
                            disabled={loading}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><ListAltIcon fontSize="small" color="primary" /></InputAdornment>,
                                inputProps: { min: 1 },
                            }}
                            helperText="Default: 20"
                        />
                    </Grid>

                    {/* Action button changes based on phase */}
                    <Grid item xs={12} sm={3}>
                        <Box className={classes.actionWrapper}>
                            {!isPreviewed ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.actionButton}
                                    onClick={handlePreview}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon />}
                                >
                                    {loading ? 'Running Preview…' : 'Preview Sync'}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={classes.actionButton}
                                        onClick={handleCommit}
                                        disabled={loading || ((results?.skipped || 0) === 0 && (results?.failed || 0) === 0)}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                                    >
                                        {loading ? 'Reconciling…' : 'Reconcile Data'}
                                    </Button>
                                    <Tooltip title="Reset and start over" arrow>
                                        <span>
                                            <IconButton
                                                className={classes.resetButton}
                                                onClick={handleReset}
                                                disabled={loading}
                                                aria-label="Reset"
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* Phase banner */}
                {(isPreviewed || isCommitted) && (
                    <Box mt={3} p={2} borderRadius={8}
                         bgcolor={isCommitted ? '#ecfdf5' : '#eff6ff'}
                         border={`1px solid ${isCommitted ? '#a7f3d0' : '#bfdbfe'}`}
                         display="flex" alignItems="center" style={{ gap: 10 }}>
                        {isCommitted
                            ? <CheckCircleIcon style={{ color: '#059669' }} />
                            : <InfoIcon style={{ color: '#2563eb' }} />}
                        <Typography variant="body2" style={{ color: isCommitted ? '#065f46' : '#1e3a8a', fontWeight: 500 }}>
                            {isCommitted
                                ? 'Reconciliation committed to Stripe. Summary below reflects the live run.'
                                : 'Preview only — no changes have been pushed to Stripe yet. Review the summary, then click Reconcile Data to commit.'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {results && (
                <Box mt={6} mb={4}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Box width={4} height={24} bgcolor="primary.main" borderRadius={2} mr={2} />
                        <Typography variant="h6" style={{ fontWeight: 800, color: '#1e293b' }}>
                            Execution Summary
                        </Typography>
                    </Box>



                    <Grid container spacing={3}>
                        {[
                            { label: 'Total', value: results.total, color: '#2563eb', icon: <SyncIcon /> },
                            { label: 'Succeeded', value: results.succeeded, color: '#059669', icon: <CheckCircleIcon /> },
                            { label: 'Skipped', value: results.skipped, color: '#64748b', icon: <InfoIcon /> },
                            { label: 'Failed', value: results.failed, color: '#dc2626', icon: <ErrorIcon /> },
                        ].map((stat, i) => (

                            <Grid item xs={6} md={3} key={i}>
                                <Paper className={classes.statCard} elevation={0}>
                                    <Box mb={1} p={1} borderRadius="50%" bgcolor={`${stat.color}15`} style={{ color: stat.color }}>
                                        {stat.icon}
                                    </Box>
                                    <Typography variant="h4" style={{ fontWeight: 900, color: stat.color }}>
                                        {stat.value || 0}
                                    </Typography>
                                    <Typography variant="overline" style={{ fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em' }}>
                                        {stat.label}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default DQPBDQReconcileTab;